import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  FileText,
  FileQuestion,
  Users,
  BarChart3,
  Eye,
  Power,
  Heart,
  Star,
  ClipboardList,
  Activity,
  Target,
  Smile,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  X,
  User,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

// Question types
const QUESTION_TYPES = [
  { value: "text", label: "Metin Yanıtı", icon: "📝" },
  { value: "boolean", label: "Evet/Hayır", icon: "✓" },
  { value: "select", label: "Tekli Seçim", icon: "☑️" },
  { value: "multiselect", label: "Çoklu Seçim", icon: "☑️" },
  { value: "rating", label: "Puan (1-5)", icon: "⭐" },
];

// Icon map
const ICON_MAP = {
  FileText,
  Heart,
  Star,
  ClipboardList,
  Activity,
  Users,
  Target,
  Smile,
  User,
};

export default function MemberFormsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "employees", "surveys"]);

  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Active forms (business's activated forms)
  const [activeForms, setActiveForms] = useState([]);
  
  // Form templates (system templates)
  const [formTemplates, setFormTemplates] = useState([]);
  
  // Selected form for viewing/editing questions
  const [selectedForm, setSelectedForm] = useState(null);
  const [formQuestions, setFormQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Responses
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [formResponses, setFormResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  
  // Create/Edit form modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    icon: "FileText",
    is_required: true,
  });
  
  // Questions modal
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    text: "",
    type: "text",
    options: [],
    is_required: true,
  });
  const [newOption, setNewOption] = useState("");
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Deactivate confirmation
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  useEffect(() => {
    fetchData();
  }, [businessId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [formsRes, templatesRes] = await Promise.all([
        fetch(`${API}/member-forms/business/${businessId}`, {
          credentials: "include",
          headers,
        }),
        fetch(`${API}/member-forms/types`, {
          credentials: "include",
          headers,
        }),
      ]);

      if (formsRes.ok) {
        setActiveForms(await formsRes.json());
      }
      if (templatesRes.ok) {
        setFormTemplates(await templatesRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch form questions
  const fetchFormQuestions = async (formId, isTemplate = false) => {
    setLoadingQuestions(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const url = isTemplate
        ? `${API}/member-forms/types/${formId}`
        : `${API}/member-forms/business/${businessId}/form/${formId}`;
      
      const response = await fetch(url, {
        credentials: "include",
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormQuestions(data.questions || []);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Fetch form responses
  const fetchFormResponses = async (formId) => {
    setLoadingResponses(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API}/member-forms/business/${businessId}/form/${formId}/responses`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      
      if (response.ok) {
        setFormResponses(await response.json());
      }
    } catch (error) {
      console.error("Error fetching responses:", error);
    } finally {
      setLoadingResponses(false);
    }
  };

  // Activate form from template
  const handleActivateTemplate = async (template) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API}/member-forms/business/${businessId}/activate`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ form_type_id: template.type_id }),
        }
      );

      if (response.ok) {
        toast.success(t("surveys:toast_formAktiflestirildi"));
        fetchData();
        setActiveTab("active");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Create custom form
  const handleSaveForm = async () => {
    if (!formData.title.trim()) {
      toast.error(t("surveys:toast_formBasligiGerekli"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const token = localStorage.getItem("auth_token");
      const url = editingForm
        ? `${API}/member-forms/business/${businessId}/form/${editingForm.form_id}`
        : `${API}/member-forms/business/${businessId}/custom`;
      
      const response = await fetch(url, {
        method: editingForm ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingForm ? t('surveys:formUpdated') : t('surveys:formCreated'));
        fetchData();
        handleCloseFormModal();
        setActiveTab("active");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  // Deactivate form
  const handleDeactivateForm = async () => {
    if (!deactivateTarget) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API}/member-forms/business/${businessId}/form/${deactivateTarget.form_id}/deactivate`,
        {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success(t("surveys:toast_formDevreDisiBirakildi"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setShowDeactivateDialog(false);
      setDeactivateTarget(null);
    }
  };

  // Delete form
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      let url = "";
      
      if (deleteTarget.type === "form") {
        url = `${API}/member-forms/business/${businessId}/form/${deleteTarget.id}`;
      } else if (deleteTarget.type === "question") {
        url = `${API}/member-forms/business/${businessId}/form/${deleteTarget.formId}/questions/${deleteTarget.id}`;
      }
      
      const response = await fetch(url, {
        method: "DELETE",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Silindi");
        if (deleteTarget.type === "form") {
          fetchData();
        } else {
          fetchFormQuestions(deleteTarget.formId, false);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  // Save question
  const handleSaveQuestion = async () => {
    if (!questionForm.text.trim()) {
      toast.error(t('common:questionTextRequired'));
      return;
    }
    
    if ((questionForm.type === "select" || questionForm.type === "multiselect") && questionForm.options.length < 2) {
      toast.error(t("surveys:toast_secimliSorularIcinEnAz"));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const token = localStorage.getItem("auth_token");
      const formId = selectedForm?.form_id || selectedForm?.type_id;
      const isTemplate = !!selectedForm?.type_id && !selectedForm?.form_id;
      
      const url = editingQuestion
        ? `${API}/member-forms/business/${businessId}/form/${formId}/questions/${editingQuestion.question_id}`
        : `${API}/member-forms/business/${businessId}/form/${formId}/questions`;
      
      const response = await fetch(url, {
        method: editingQuestion ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(questionForm),
      });

      if (response.ok) {
        toast.success(editingQuestion ? t("surveys:questionUpdated") : t("surveys:questionAdded"));
        fetchFormQuestions(formId, isTemplate);
        handleCloseQuestionModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  // Modal handlers
  const handleOpenFormModal = (form = null) => {
    if (form) {
      setEditingForm(form);
      setFormData({
        title: form.title || form.name,
        description: form.description || "",
        icon: form.icon || "FileText",
        is_required: form.is_required ?? true,
      });
    } else {
      setEditingForm(null);
      setFormData({
        title: "",
        description: "",
        icon: "FileText",
        is_required: true,
      });
    }
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setEditingForm(null);
    setFormData({
      title: "",
      description: "",
      icon: "FileText",
      is_required: true,
    });
  };

  const handleOpenQuestionsModal = async (form, isTemplate = false) => {
    setSelectedForm({ ...form, isTemplate });
    setShowQuestionsModal(true);
    await fetchFormQuestions(form.form_id || form.type_id, isTemplate);
  };

  const handleCloseQuestionsModal = () => {
    setShowQuestionsModal(false);
    setSelectedForm(null);
    setFormQuestions([]);
  };

  const handleOpenResponsesModal = async (form) => {
    setSelectedForm(form);
    setShowResponsesModal(true);
    await fetchFormResponses(form.form_id);
  };

  const handleOpenQuestionModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        text: question.text,
        type: question.type,
        options: question.options || [],
        is_required: question.is_required ?? true,
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        text: "",
        type: "text",
        options: [],
        is_required: true,
      });
    }
    setNewOption("");
    setShowAddQuestionModal(true);
  };

  const handleCloseQuestionModal = () => {
    setShowAddQuestionModal(false);
    setEditingQuestion(null);
  };

  const handleAddOption = () => {
    if (newOption.trim() && !questionForm.options.includes(newOption.trim())) {
      setQuestionForm({
        ...questionForm,
        options: [...questionForm.options, newOption.trim()],
      });
      setNewOption("");
    }
  };

  const handleRemoveOption = (opt) => {
    setQuestionForm({
      ...questionForm,
      options: questionForm.options.filter((o) => o !== opt),
    });
  };

  const getQuestionTypeLabel = (type) => {
    const labelMap = {
      "text": t('surveys:questionTypeText'),
      "boolean": t('surveys:questionTypeBoolean'),
      "select": t('surveys:questionTypeSelect'),
      "multiselect": t('surveys:questionTypeMultiselect'),
      "rating": t('surveys:questionTypeRating'),
    };
    return labelMap[type] || type;
  };

  const translatedQuestionTypes = QUESTION_TYPES.map(qt => ({
    ...qt,
    label: {
      "Metin Yanıtı": t('surveys:questionTypeText'),
      "Evet/Hayır": t('surveys:questionTypeBoolean'),
      "Tekli Seçim": t('surveys:questionTypeSelect'),
      "Çoklu Seçim": t('surveys:questionTypeMultiselect'),
      "Puan (1-5)": t('surveys:questionTypeRating'),
    }[qt.label] || qt.label,
  }));

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || FileText;
  };

  // Check if template is already activated
  const isTemplateActivated = (typeId) => {
    return activeForms.some((f) => f.form_type_id === typeId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">{t("surveys:memberForms")}</h1>
          <p className="text-grey text-sm">
            {t('employees:memberFormDesc')}
          </p>
        </div>
        <Button
          onClick={() => {
            setActiveTab("create");
            handleOpenFormModal();
          }}
          className="bg-navy hover:bg-navy/90 rounded-xl"
          data-testid="create-form-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('surveys:customFormCreate')}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start rounded-xl bg-white p-1 shadow-sm">
          <TabsTrigger value="active" className="rounded-lg">
            {t('surveys:activeFormsTab')} ({activeForms.length})
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-lg">
            {t('common:templates')}
          </TabsTrigger>
        </TabsList>

        {/* Active Forms Tab */}
        <TabsContent value="active" className="mt-4">
          {activeForms.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey mb-4">{t("surveys:henuzAktifFormYok")}</p>
                <p className="text-sm text-grey mb-4">
                  {t('surveys:activateFromTemplates')}
                </p>
                <Button
                  onClick={() => setActiveTab("templates")}
                  className="bg-navy hover:bg-navy/90 rounded-xl"
                >
                  {t('common:goToTemplates')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {activeForms.map((form) => {
                const IconComp = getIconComponent(form.icon);
                return (
                  <Card
                    key={form.form_id}
                    className="border-0 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="p-3 rounded-xl bg-orange-100 text-orange-700">
                            <IconComp className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-navy truncate">
                                {form.title || form.name}
                              </h3>
                              {form.form_type_id && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  {t('common:system')}
                                </Badge>
                              )}
                              {form.is_required && (
                                <Badge className="bg-orange-100 text-orange-700 text-xs">
                                  {t('common:required')}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-grey">
                              <span>{form.questions?.length || form.question_count || 0} soru</span>
                              <span>•</span>
                              <span>{form.response_count || 0} yanıt</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenQuestionsModal(form, false)}
                            className="rounded-xl"
                            data-testid={`form-questions-${form.form_id}`}
                          >
                            <FileQuestion className="w-4 h-4 mr-1" />
                            Sorular
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenResponsesModal(form)}
                            className="rounded-xl"
                            data-testid={`form-responses-${form.form_id}`}
                          >
                            <BarChart3 className="w-4 h-4 mr-1" />
                            {t('surveys:answers')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenFormModal(form)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeactivateTarget(form);
                              setShowDeactivateDialog(true);
                            }}
                            className="h-8 w-8 p-0 text-amber-500"
                            title={t("admin:ph_devreDisiBirak")}
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid gap-4">
            {formTemplates.map((template) => {
              const IconComp = getIconComponent(template.icon);
              const isActivated = isTemplateActivated(template.type_id);
              return (
                <Card
                  key={template.type_id}
                  className={`border-0 shadow-sm hover:shadow-md transition-shadow ${
                    isActivated ? "opacity-60" : ""
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="p-3 rounded-xl bg-blue-100 text-blue-700">
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-navy truncate">
                              {template.name}
                            </h3>
                            {template.is_system && (
                              <Badge className="bg-slate-100 text-slate-700 text-xs">
                                {t('common:systemTemplate')}
                              </Badge>
                            )}
                            {isActivated && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {t('admin:active')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-grey mt-1 line-clamp-1">
                            {template.description}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-grey">
                            <span>{template.questions?.length || 0} {t('surveys:question')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenQuestionsModal(template, true)}
                          className="rounded-xl"
                        >
                          <FileQuestion className="w-4 h-4 mr-1" />
                          Sorular
                        </Button>
                        {!isActivated ? (
                          <Button
                            size="sm"
                            onClick={() => handleActivateTemplate(template)}
                            className="bg-navy hover:bg-navy/90 rounded-xl"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            {t('admin:activate')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            className="rounded-xl"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Aktif
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {formTemplates.length === 0 && (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                  <p className="text-grey">{t("common:henuzSablonBulunmuyor")}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Form Modal */}
      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingForm ? t('common:editForm') : t('common:createNewForm')}
            </DialogTitle>
            <DialogDescription>
              Üyelerinizin doldurması gereken özel bir form oluşturun
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('surveys:formTitle')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder={t("surveys:ph_ornSaglikBeyaniFormu")}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>{t('common:description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t("surveys:ph_formHakkindaKisaBirAciklama")}
                className="rounded-xl mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>{t("common:icon")}</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {Object.entries(ICON_MAP).map(([name, Icon]) => (
                  <Button
                    key={name}
                    type="button"
                    variant={formData.icon === name ? "default" : "outline"}
                    className={`w-10 h-10 p-0 rounded-xl ${
                      formData.icon === name ? "bg-navy" : ""
                    }`}
                    onClick={() => setFormData({ ...formData, icon: name })}
                  >
                    <Icon className="w-5 h-5" />
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
              <Switch
                checked={formData.is_required}
                onCheckedChange={(v) =>
                  setFormData({ ...formData, is_required: v })
                }
              />
              <div>
                <Label className="cursor-pointer">{t('admin:requiredForm')}</Label>
                <p className="text-xs text-grey">
                  {t('surveys:requiredFormsShownFirst')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleCloseFormModal}
              className="rounded-xl"
              disabled={isSaving}
            >
              {t('common:cancel')}
            </Button>
            <Button
              onClick={handleSaveForm}
              disabled={!formData.title.trim() || isSaving}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {isSaving ? t('common:saving') : (editingForm ? t('settings:update') : t('settings:create'))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Questions Modal */}
      <Dialog open={showQuestionsModal} onOpenChange={setShowQuestionsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5" />
              {selectedForm?.title || selectedForm?.name} - {t('surveys:questions')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-grey">
                {selectedForm?.isTemplate
                  ? t('surveys:templateQuestionsReadonly')
                  : t('surveys:editFormQuestions')}
              </p>
              {!selectedForm?.isTemplate && (
                <Button
                  onClick={() => handleOpenQuestionModal()}
                  className="bg-navy hover:bg-navy/90 rounded-xl"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t('surveys:addQuestion')}
                </Button>
              )}
            </div>

            {loadingQuestions ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : formQuestions.length === 0 ? (
              <p className="text-center text-grey py-8">{t("surveys:noQuestionsYet")}</p>
            ) : (
              <div className="space-y-3">
                {formQuestions.map((question, index) => (
                  <div
                    key={question.question_id || index}
                    className="flex items-start justify-between bg-slate-50 rounded-xl p-4 group"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-grey font-medium">
                          #{index + 1}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {getQuestionTypeLabel(question.type)}
                        </Badge>
                        {question.is_required && (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            {t('common:required')}
                          </Badge>
                        )}
                      </div>
                      <p className="font-medium text-navy">{question.text}</p>
                      {question.options && question.options.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {question.options.map((opt, i) => (
                            <span
                              key={i}
                              className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded"
                            >
                              {opt}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {!selectedForm?.isTemplate && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenQuestionModal(question)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => {
                            setDeleteTarget({
                              type: "question",
                              id: question.question_id,
                              formId: selectedForm.form_id,
                              name: question.text,
                            });
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={handleCloseQuestionsModal}
              className="rounded-xl"
            >
              {t('common:close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Question Modal */}
      <Dialog open={showAddQuestionModal} onOpenChange={setShowAddQuestionModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? t('surveys:editQuestion') : t('common:addNewQuestion')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("surveys:questionText")}</Label>
              <Textarea
                value={questionForm.text}
                onChange={(e) =>
                  setQuestionForm({ ...questionForm, text: e.target.value })
                }
                placeholder={t("surveys:ph_soruMetniniYazin")}
                className="rounded-xl mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>{t("surveys:questionType")}</Label>
              <Select
                value={questionForm.type}
                onValueChange={(v) =>
                  setQuestionForm({ ...questionForm, type: v, options: [] })
                }
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {translatedQuestionTypes.map((qt) => (
                    <SelectItem key={qt.value} value={qt.value}>
                      {qt.icon} {qt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(questionForm.type === "select" ||
              questionForm.type === "multiselect") && (
              <div>
                <Label>{t("common:options")}</Label>
                <div className="space-y-2 mt-2">
                  {questionForm.options.map((opt, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="flex-1 bg-slate-50 px-3 py-2 rounded-xl text-sm">
                        {opt}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleRemoveOption(opt)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder={t("common:newOption")}
                      className="rounded-xl flex-1"
                      onKeyPress={(e) =>
                        e.key === "Enter" && (e.preventDefault(), handleAddOption())
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddOption}
                      className="rounded-xl"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
              <Switch
                checked={questionForm.is_required}
                onCheckedChange={(v) =>
                  setQuestionForm({ ...questionForm, is_required: v })
                }
              />
              <Label className="cursor-pointer">{t('surveys:thisQuestionRequired')}</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleCloseQuestionModal}
              className="rounded-xl"
              disabled={isSaving}
            >
              {t('common:cancel')}
            </Button>
            <Button
              onClick={handleSaveQuestion}
              disabled={!questionForm.text.trim() || isSaving}
              className="bg-navy hover:bg-navy/90 rounded-xl"
            >
              {isSaving ? t('common:saving') : (editingQuestion ? t('settings:update') : t('common:add'))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Responses Modal */}
      <Dialog open={showResponsesModal} onOpenChange={setShowResponsesModal}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              {selectedForm?.title || selectedForm?.name} - {t('surveys:answers')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingResponses ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : formResponses.length === 0 ? (
              <p className="text-center text-grey py-8">{t("surveys:henuzYanitYok")}</p>
            ) : (
              <div className="space-y-3">
                {formResponses.map((response) => (
                  <Card
                    key={response.response_id}
                    className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedResponse(response)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-navy" />
                          </div>
                          <div>
                            <p className="font-medium text-navy">
                              {response.member_name || t('common:member')}
                            </p>
                            <p className="text-sm text-grey">
                              {response.completed_at &&
                                format(
                                  new Date(response.completed_at),
                                  "d MMMM yyyy HH:mm",
                                  { locale: getDateLocale() }
                                )}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="rounded-xl">
                          <Eye className="w-4 h-4 mr-1" />
                          {t('common:detail')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowResponsesModal(false);
                setSelectedResponse(null);
              }}
              className="rounded-xl"
            >
              {t('common:close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Response Detail Modal */}
      {selectedResponse && (
        <Dialog
          open={!!selectedResponse}
          onOpenChange={() => setSelectedResponse(null)}
        >
          <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedResponse.member_name || t('common:member')} - {t('common:responseDetail')}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {selectedResponse.questions?.map((q, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-xl">
                  <p className="font-medium text-navy mb-2">
                    {index + 1}. {q.text}
                  </p>
                  <p className="text-grey">
                    {typeof selectedResponse.answers?.[q.question_id] === "boolean"
                      ? selectedResponse.answers[q.question_id]
                        ? t('common:yes')
                        : t('common:no')
                      : Array.isArray(selectedResponse.answers?.[q.question_id])
                      ? selectedResponse.answers[q.question_id].join(", ")
                      : selectedResponse.answers?.[q.question_id] || "-"}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setSelectedResponse(null)}
                className="rounded-xl"
              >
                {t('common:close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {t('admin:deleteConfirmation')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="font-semibold text-navy">
                  &quot;{deleteTarget?.name || t('common:thisItem')}&quot;
                </span>{" "}
                {t('admin:areYouSureDelete')}
                <br />
                <span className="text-red-500 text-sm">
                  {t('common:thisActionCannotBeUndone')}
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {t('common:delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Power className="w-5 h-5 text-amber-500" />
              {t('common:disable')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="font-semibold text-navy">
                  &quot;{deactivateTarget?.title || deactivateTarget?.name}&quot;
                </span>{" "}
                {t('common:disableFormConfirm')}
                <br />
                <span className="text-amber-600 text-sm">
                  {t('common:membersWontSeeForm')}
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateForm}
              className="bg-amber-500 hover:bg-amber-600 rounded-xl"
            >
              {t('common:disable')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
